import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { track } from "@/lib/signals";

// Route patterns from App.tsx. Only the pattern is recorded, never ids or slugs.
// Two pages are folded into a neighbour so a page view can't reveal an
// anonymous action: the survey respond page (anonymous-survey participation)
// and My contributions (being a Database contributor).
const ROUTES: [pattern: string, recordAs?: string][] = [
  ["/"], ["/poise"], ["/login"], ["/register"], ["/signup-success"], ["/activate-account"],
  ["/verify-email"], ["/docs"], ["/forgot-password"], ["/privacy-policy"], ["/terms-of-service"],
  ["/contact"], ["/reactivate-account"], ["/why-verify"],
  ["/feed"], ["/feed/:postId"], ["/notifications"], ["/onboarding"],
  ["/database"], ["/database/folders/:folderId"], ["/database/documents/:documentId"], ["/database/contributions", "/database"],
  ["/surveys"], ["/surveys/mine"], ["/surveys/create"], ["/surveys/:id/results"], ["/surveys/:id/edit"],
  ["/surveys/:id/respond", "/surveys/:id"], ["/surveys/:id"],
  ["/events"], ["/events/create"], ["/events/:id/edit"], ["/events/:id"],
  ["/documents"], ["/vendors"], ["/vendors/new"], ["/vendors/:slug"], ["/insights"], ["/resources"], ["/settings"],
  ["/companies"], ["/companies/:slug"],
  // Rooms. The pattern is recorded, never the value: a room id says which
  // closed room someone opened, and an invite token is a key to one.
  ["/rooms"], ["/rooms/:roomId"], ["/rooms/invite/:token"],
  ["/employment-verification"], ["/employment-verify"], ["/profile"],
];

const COMING_SOON: Record<string, string> = {
  "/documents": "documents",
  "/insights": "insights",
  "/resources": "resources",
};

const SIGNUP_STEPS: Record<string, string> = {
  "/": "landing",
  "/register": "register",
  "/signup-success": "submitted",
};

export function routePattern(pathname: string): string {
  for (const [pattern, recordAs] of ROUTES) {
    if (matchPath({ path: pattern, end: true }, pathname)) return recordAs ?? pattern;
  }
  return "other";
}

function signedIn() {
  try {
    return Boolean(localStorage.getItem("access_token"));
  } catch {
    return false;
  }
}

/**
 * Records a page view per pathname. Waits a moment so the instant redirects
 * done by route guards (e.g. / → /feed) count as one view, not two.
 */
export default function SignalsRouteTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      const route = routePattern(pathname);
      track("page.viewed", { props: { route, app: "main" } });
      if (COMING_SOON[route]) track("coming_soon.visited", { props: { feature: COMING_SOON[route] } });
      if (SIGNUP_STEPS[route] && !signedIn()) track("signup.funnel", { props: { step: SIGNUP_STEPS[route] } });
    }, 400);
    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}
